import { AppDispatch } from '../store/store';
import { setUser } from '../store/userSlice';
import { auth } from '../firebase';
import { signInWithCustomToken } from 'firebase/auth';
import { callAPI } from './api.service';
import { HTTPStatusEnum } from '../interfaces/log.interface';

export interface GoogleClassroomAuthResponse {
  user: any;
  token: string;
  isNewUser: boolean;
}

export class GoogleClassroomService {
  /**
   * Handle OAuth callback from Google Classroom
   * @param code Authorization code from OAuth callback
   * @param state State parameter from OAuth callback
   * @returns Authentication response
   */
  static async handleCallback(code: string, state?: string): Promise<GoogleClassroomAuthResponse> {
    try {
      const response = await callAPI('api/auth/google-classroom/callback', {
        code,
        state,
      }, 'POST');

      if (response?.status !== HTTPStatusEnum.OK) {
        throw new Error(response?.error || 'Authentication failed');
      }

      return {
        user: response.user,
        token: response.token,
        isNewUser: response.isNewUser,
      };
    } catch (error: any) {
      console.error('Error handling Google Classroom callback:', error);
      throw new Error(error.message || 'Authentication failed');
    }
  }

  /**
   * Complete the authentication flow
   * @param authResponse Authentication response from callback
   * @param dispatch Redux dispatch function
   */
  static async completeAuth(authResponse: GoogleClassroomAuthResponse, dispatch: AppDispatch): Promise<void> {
    try {
      // Sign in to Firebase with custom token
      const userCredential = await signInWithCustomToken(auth, authResponse.token);
      
      // Update Redux store
      dispatch(setUser({
        ...userCredential.user,
        ...authResponse.user,
      }));

      console.log('Google Classroom authentication completed successfully');
    } catch (error: any) {
      console.error('Error completing Google Classroom authentication:', error);
      throw new Error('Failed to complete authentication');
    }
  }

  /**
   * Sign in with Google Classroom
   * @param code Authorization code
   * @returns Authentication response
   */
  static async signIn(code: string): Promise<GoogleClassroomAuthResponse> {
    try {
      const response = await callAPI('api/auth/google-classroom/callback', {
        code,
      }, 'POST');

      if (response?.status !== HTTPStatusEnum.OK) {
        throw new Error(response?.error || 'Sign in failed');
      }

      return {
        user: response.user,
        token: response.token,
        isNewUser: response.isNewUser,
      };
    } catch (error: any) {
      console.error('Error signing in with Google Classroom:', error);
      throw new Error(error.message || 'Sign in failed');
    }
  }

  /**
   * Link existing account to Google Classroom
   * @param email User's email
   * @param code Authorization code
   * @returns Updated user data
   */
  static async linkAccount(email: string, code: string): Promise<any> {
    try {
      const response = await callAPI('api/auth/google-classroom/link', {
        email,
        code,
      }, 'POST');

      if (response?.status !== HTTPStatusEnum.OK) {
        throw new Error(response?.error || 'Failed to link account');
      }

      return response.user;
    } catch (error: any) {
      console.error('Error linking account to Google Classroom:', error);
      throw new Error(error.message || 'Failed to link account');
    }
  }

  /**
   * Get user information from Google Classroom
   * @param accessToken Access token
   * @returns User information
   */
  static async getUserInfo(accessToken: string): Promise<any> {
    try {
      const response = await callAPI(`api/auth/google-classroom/user-info?accessToken=${accessToken}`, null, 'GET');

      if (response?.status !== HTTPStatusEnum.OK) {
        throw new Error(response?.error || 'Failed to get user info');
      }

      return response.user;
    } catch (error: any) {
      console.error('Error getting user info from Google Classroom:', error);
      throw new Error(error.message || 'Failed to get user info');
    }
  }

  /**
   * Refresh Google Classroom access token
   * @param refreshToken Refresh token
   * @returns New token response
   */
  static async refreshToken(refreshToken: string): Promise<any> {
    try {
      const response = await callAPI('api/auth/google-classroom/refresh', {
        refreshToken,
      }, 'POST');

      if (response?.status !== HTTPStatusEnum.OK) {
        throw new Error(response?.error || 'Failed to refresh token');
      }

      return response.token;
    } catch (error: any) {
      console.error('Error refreshing Google Classroom token:', error);
      throw new Error(error.message || 'Failed to refresh token');
    }
  }
}
